import { Check, Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PlanStepState = 'done' | 'current' | 'todo';

export interface PlanStep {
  id: string;
  label: string;
  detail?: string;
  state: PlanStepState;
  icon?: LucideIcon;
}

const STAGGER_MS = 90;

/** The plan's stages, one per row. Its look lives in this file only, so it can follow the studio's progress design. */
export function PlanSteps({ steps, working = true }: { steps: PlanStep[]; working?: boolean }) {
  return (
    <ol className="relative space-y-3 pl-1" aria-label="The plan">
      {steps.map((step, i) => {
        const Icon = step.icon;
        return (
          <li
            key={step.id}
            data-state={step.state}
            className="plan-rise relative flex gap-3"
            style={{ animationDelay: `${i * STAGGER_MS}ms` }}
          >
            {i < steps.length - 1 && <span aria-hidden className="absolute left-[13px] top-8 h-[calc(100%-20px)] w-px bg-b-line-strong" />}
            <span
              className={cn(
                'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--b-radius-control)] bg-b-raised text-b-t2',
                step.state === 'current' && 'bg-b-ink text-b-on-ink',
              )}
            >
              {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
            </span>
            <div className="min-w-0 pt-0.5">
              <p className="flex items-center gap-1.5 text-b-label text-b-t1">
                {step.label}
                {step.state === 'done' && <Check className="h-3.5 w-3.5 text-b-t2" aria-label="done" />}
                {step.state === 'current' && working && <Loader2 className="h-3.5 w-3.5 animate-spin text-b-t3" aria-label="in progress" />}
              </p>
              {/* Not cn(): tailwind-merge reads the B size and the B colour as one group and drops the size. */}
              {step.detail && <p className={`text-b-read ${step.state === 'todo' ? 'text-b-t3' : 'text-b-t2'}`}>{step.detail}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
